import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HouseholdMember } from '@chore-project/shared';
import { useActiveMemberContext } from '../context/ActiveMemberContext';
import { useAllHouseholdMembers, useAllHouseholdSettings } from './useHousehold';
import { useSelectedHousehold } from './useSelectedHousehold';

/**
 * Resolves the "acting as" identity. Returns the shared member currently
 * switched into (or null = the signed-in primary), the shared members the
 * caller may switch into, and a `switchTo` that flips identity and refetches
 * the per-user dashboard data.
 *
 * The switchable list is user-scoped, not household-scoped: it spans every
 * household the primary belongs to, so the switcher works whichever household
 * is selected. (While acting as a shared user, `useSelectedHousehold` locks
 * the household list to that user's household — which also keeps these
 * queries within what the API's acting-as guard permits.)
 */
export function useActiveMember(primarySub: string) {
  const { households, isLoading: householdsLoading } = useSelectedHousehold();
  const householdIds = (households ?? []).map((h) => h.householdId);
  const {
    data: members,
    isLoading: membersLoading,
    isError: membersError,
  } = useAllHouseholdMembers(householdIds);
  const { data: settingsById } = useAllHouseholdSettings(householdIds);
  const { activeMemberId, setActiveMember } = useActiveMemberContext();
  const queryClient = useQueryClient();

  // Any member may switch when the shared user's own household allows it
  // (default true); otherwise only the shared user's creator can.
  const sharedMembers = members.filter((m) => m.isShared);
  const switchable = sharedMembers.filter(
    (m) =>
      settingsById.get(m.householdId)?.allowSharedSwitchByAll !== false ||
      m.managedBy === primarySub,
  );
  const activeMember: HouseholdMember | null =
    (activeMemberId && sharedMembers.find((m) => m.userId === activeMemberId)) || null;

  // If the persisted active member is no longer a switchable shared member of its
  // household — e.g. it was deleted, or switching was disallowed — fall back to self.
  useEffect(() => {
    // Wait for the household list and every member query — an empty merged
    // array during load (or a transient fetch error) must not reset a
    // persisted acting-as identity.
    if (!activeMemberId || householdsLoading || membersLoading || membersError) return;
    if (!switchable.some((m) => m.userId === activeMemberId)) {
      setActiveMember(null);
    }
  }, [activeMemberId, householdsLoading, membersLoading, switchable, setActiveMember]);

  const invalidatePerUser = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['incoming-shares'] });
    void queryClient.invalidateQueries({ queryKey: ['points'] });
  }, [queryClient]);

  const switchTo = useCallback(
    (member: HouseholdMember | null) => {
      setActiveMember(member ? { id: member.userId, householdId: member.householdId } : null);
      // The per-user dashboard data depends on the acting-as identity.
      invalidatePerUser();
    },
    [setActiveMember, invalidatePerUser],
  );

  return {
    activeMemberId,
    activeMember,
    switchableSharedMembers: switchable,
    isActingAsShared: !!activeMember,
    switchTo,
  };
}
